import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { mediaUrl } from '@/lib/mediaUrl';
import { toast } from 'sonner';
import { Search, Clock, ArrowRight, FileDown, BookOpen, Newspaper, Download, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';

const TABS = [
  { id: 'articles', label: 'Articles & Guides', icon: Newspaper },
  { id: 'downloads', label: 'Free Downloads', icon: FileDown },
  { id: 'books', label: 'E-books & Reading', icon: BookOpen },
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const ArticleCard = ({ a }) => (
  <Link to={`/knowledge-hub/${a.slug}`} className="group flex flex-col bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_12px_32px_-12px_rgba(42,62,93,0.25)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300" data-testid={`hub-article-${a.slug}`}>
    {a.cover_url ? <img src={mediaUrl(a.cover_url)} alt="" className="h-44 w-full object-cover" /> : (
      <div className="h-44 bg-[#2a3e5d] relative overflow-hidden flex items-end p-5">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#02d1d4]/25" />
        <div className="absolute right-16 top-14 w-20 h-20 rounded-full bg-[#20acf6]/30" />
        <span className="relative text-[11px] uppercase tracking-[0.14em] font-semibold text-[#02d1d4]">{a.category}</span>
      </div>
    )}
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-bold text-[#2a3e5d] text-lg leading-snug group-hover:text-[#20acf6] transition-colors">{a.title}</h3>
      <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{a.excerpt}</p>
      <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.read_minutes || 5} min read · {fmtDate(a.published_at)}</span>
        <ArrowRight className="h-4 w-4 text-[#20acf6] group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  </Link>
);

const Articles = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/hub/articles`, { params: { q: q || undefined, category: cat || undefined, limit: 50 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], categories: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat]);
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative md:w-96">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles and guides..." className="pl-10 h-11 rounded-xl bg-white" data-testid="hub-search-input" />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="hub-category-filters">
          {['', ...(data?.categories || [])].map((c) => (
            <button key={c || 'all'} onClick={() => setCat(c)} data-testid={`hub-filter-${c || 'all'}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c ? 'bg-[#2a3e5d] text-white border-[#2a3e5d]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#20acf6] hover:text-[#2a3e5d]'}`}>
              {c || 'All topics'}
            </button>
          ))}
        </div>
      </div>
      {!data ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#20acf6]" /></div>
        : data.items.length === 0 ? <p className="text-slate-500 py-10 text-center" data-testid="hub-no-results">No articles match your search.</p>
          : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.items.map((a) => <ArticleCard key={a.id} a={a} />)}</div>}
    </div>
  );
};

const DownloadGate = ({ item, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', organisation: '', consent_marketing: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { setDone(null); }, [item]);
  if (!item) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hub/downloads/${item.id}/request`, form);
      setDone(r.data.file_url);
      window.open(mediaUrl(r.data.file_url), '_blank', 'noopener');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Please check your details');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="download-gate-dialog">
        <DialogHeader>
          <DialogTitle>{done ? 'Your download is ready' : 'Get your free download'}</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center space-y-4 py-2">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <p className="text-sm text-slate-600">We've also emailed you the link.</p>
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]"><a href={mediaUrl(done)} target="_blank" rel="noopener noreferrer" data-testid="download-file-link"><Download className="h-4 w-4 mr-2" />Open PDF</a></Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="download-name" />
            <Input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="download-email" />
            <Input placeholder="Organisation (optional)" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} data-testid="download-org" />
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" className="mt-0.5 accent-[#20acf6]" checked={form.consent_marketing} onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })} data-testid="download-consent" />
              Send me occasional updates about new courses and resources. You can unsubscribe at any time.
            </label>
            <Button type="submit" disabled={busy} className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="download-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Download free
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Downloads = () => {
  const [items, setItems] = useState(null);
  const [gate, setGate] = useState(null);
  useEffect(() => { axios.get(`${API}/hub/downloads`).then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#20acf6]" /></div>;
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-slate-200/80 p-6 flex flex-col shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_12px_32px_-12px_rgba(42,62,93,0.25)] transition-shadow" data-testid={`hub-download-${d.id}`}>
            <div className="w-12 h-12 rounded-xl bg-[#02d1d4]/15 flex items-center justify-center mb-4"><FileDown className="h-6 w-6 text-[#0aa5a8]" /></div>
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#20acf6]">{d.category} · {d.file_type}</span>
            <h3 className="font-bold text-[#2a3e5d] mt-1.5 leading-snug">{d.title}</h3>
            <p className="text-sm text-slate-600 mt-2 flex-1">{d.description}</p>
            <Button onClick={() => setGate(d)} variant="outline" className="mt-5 rounded-xl border-[#2a3e5d]/20 hover:bg-[#2a3e5d] hover:text-white" data-testid={`download-btn-${d.id}`}>
              <Download className="h-4 w-4 mr-2" /> Free download
            </Button>
          </div>
        ))}
      </div>
      <DownloadGate item={gate} onClose={() => setGate(null)} />
    </>
  );
};

const Books = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [owned, setOwned] = useState([]);
  useEffect(() => { axios.get(`${API}/hub/products`).then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { if (user) api.get('/hub/my-products').then((r) => setOwned(r.data.map((p) => p.id))).catch(() => {}); }, [api, user]);
  const download = async (p) => {
    try { const r = await api.get(`/hub/products/${p.id}/download`); window.open(mediaUrl(r.data.file_url), '_blank', 'noopener'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Download unavailable'); }
  };
  const [guestBook, setGuestBook] = useState(null);
  const checkout = async (p, email) => {
    setBusy(p.id);
    try {
      const r = await (user ? api : axios).post(`${user ? '' : API}/hub/products/${p.id}/checkout`, { origin_url: window.location.origin, email: email || undefined });
      window.location.href = r.data.url;
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not start checkout'); setBusy(null); }
  };
  const buy = (p) => (user ? checkout(p) : setGuestBook(p));
  if (!items) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#20acf6]" /></div>;
  return (
    <>
    <GuestBuyDialog book={guestBook} busy={!!busy} onClose={() => setGuestBook(null)} onSubmit={(email) => checkout(guestBook, email)} onLogin={() => navigate('/login', { state: { from: { pathname: '/knowledge-hub', search: '?tab=books' } } })} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {items.map((b) => (
        <div key={b.id} className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden flex flex-col" data-testid={`hub-book-${b.id}`}>
          {b.cover_url ? <img src={mediaUrl(b.cover_url)} alt={b.title} className="h-52 w-full object-cover" /> : (
            <div className="h-52 bg-gradient-to-b from-[#2a3e5d] to-[#1e2d45] flex items-center justify-center p-6 text-center">
              <div className="border border-white/25 rounded-md px-4 py-6 w-32 h-40 flex flex-col justify-between bg-white/5">
                <BookOpen className="h-5 w-5 text-[#02d1d4] mx-auto" />
                <span className="text-white text-[11px] font-semibold leading-tight">{b.title}</span>
              </div>
            </div>
          )}
          <div className="p-5 flex flex-col flex-1">
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#20acf6]">{b.format}</span>
            <h3 className="font-bold text-[#2a3e5d] mt-1 leading-snug">{b.title}</h3>
            {b.author && <p className="text-xs text-slate-500 mt-0.5">by {b.author}</p>}
            <p className="text-sm text-slate-600 mt-2 flex-1 line-clamp-3">{b.description}</p>
            {owned.includes(b.id) ? (
              <Button onClick={() => download(b)} className="mt-4 rounded-xl bg-green-600 hover:bg-green-700" data-testid={`book-download-${b.id}`}><Download className="h-4 w-4 mr-2" /> Download your e-book</Button>
            ) : b.external_url ? (
              <Button asChild variant="outline" className="mt-4 rounded-xl" data-testid={`book-store-link-${b.id}`}>
                <a href={b.external_url} target="_blank" rel="noopener noreferrer sponsored">View in store <ExternalLink className="h-3.5 w-3.5 ml-2" /></a>
              </Button>
            ) : (
              <Button onClick={() => buy(b)} disabled={busy === b.id} className="mt-4 rounded-xl bg-[#2a3e5d] hover:bg-[#1e293b]" data-testid={`book-buy-${b.id}`}>
                {busy === b.id && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Buy e-book · £{Number(b.price).toFixed(2)}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
    </>
  );
};

const GuestBuyDialog = ({ book, busy, onClose, onSubmit, onLogin }) => {
  const [email, setEmail] = useState('');
  if (!book) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="guest-buy-dialog">
        <DialogHeader>
          <DialogTitle>Buy “{book.title}”</DialogTitle>
          <DialogDescription>Enter your email — we'll send your private download link there after payment. No account needed.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(email); }} className="space-y-3">
          <Input required type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="guest-buy-email" />
          <Button type="submit" disabled={busy} className="w-full bg-[#2a3e5d] hover:bg-[#1e293b]" data-testid="guest-buy-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Continue to payment · £{Number(book.price).toFixed(2)}
          </Button>
          <p className="text-xs text-slate-500 text-center">Have an account? <button type="button" onClick={onLogin} className="text-[#20acf6] font-medium hover:underline" data-testid="guest-buy-login">Sign in</button> to keep it in My e-books.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function KnowledgeHubPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'articles';
  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FC]" data-testid="knowledge-hub-page">
      <Helmet>
        <title>Knowledge Hub | Care Sector Guides & Free Resources | Common Impact</title>
        <meta name="description" content="Free guides, checklists and templates for UK care and support workers: CQC compliance, safeguarding, challenging behaviour, independent living and care careers." />
        <meta property="og:title" content="Knowledge Hub | Common Impact E-Learning" />
        <meta property="og:description" content="Expert guides, free downloadable checklists and recommended reading for the UK care sector." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      <section className="bg-[#2a3e5d] relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#02d1d4]/15" />
        <div className="absolute right-40 bottom-0 w-48 h-48 rounded-full bg-[#20acf6]/15 translate-y-1/2" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#02d1d4]">Knowledge Hub</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3 max-w-3xl leading-[1.08]">Practical knowledge for people who care</h1>
          <p className="text-base md:text-lg text-white/75 mt-5 max-w-2xl">Expert guides, free checklists and recommended reading for support workers, managers and care providers across the UK.</p>
        </div>
      </section>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-2 overflow-x-auto pb-1 mb-8 border-b border-slate-200" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setParams(id === 'articles' ? {} : { tab: id })} data-testid={`hub-tab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? 'border-[#20acf6] text-[#2a3e5d]' : 'border-transparent text-slate-500 hover:text-[#2a3e5d]'}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        {tab === 'articles' && <Articles />}
        {tab === 'downloads' && <Downloads />}
        {tab === 'books' && <Books />}
      </main>
      <Footer />
    </div>
  );
}
